<template>
  <div 
    class="c-recommendedSearchTags"
    :class="getClasses()"
  >
    <ContentHeadline
      class="c-recommendedSearchTags_headline"
      title-tag="h2"
      titleText="おすすめの検索タグ"
    />
    <div class="c-recommendedSearchTags_content">
      <!-- 最初のタググループ -->
      <div
        class="c-tagGroup c-tagGroup-first"
        ref="firstTagGroup"
      >
        <h3 class="c-tagGroup_headline">{{ firstRecommendedSearchTag.groupName }}</h3>
        <div class="c-tagGroup_list">
          <NuxtLink
            class="c-tagGroup_listItem"
            v-for="tag in firstRecommendedSearchTag.searchTagsList"
            :key="tag.url"
            :to="getTagUrl(tag.url)"
          >
            {{ tag.name }}
          </NuxtLink>
        </div>
      </div>
      <!-- 2つ目以降のタググループ -->
      <div
        v-if="recommendedSearchTagAfterFirst.length > 0"
        class="c-recommendedSearchTags_tagGroups"
      >
        <div
          v-for="recommendedSearchTag in recommendedSearchTagAfterFirst"
          :key="recommendedSearchTag.groupName"
          class="c-tagGroup"
        >
          <h3 class="c-tagGroup_headline">{{ recommendedSearchTag.groupName }}</h3>
          <div class="c-tagGroup_list">
            <NuxtLink
              class="c-tagGroup_listItem"
              v-for="tag in recommendedSearchTag.searchTagsList"
              :key="tag.url"
              :to="getTagUrl(tag.url)"
            >
              {{ tag.name }}
            </NuxtLink>
          </div>
        </div>
      </div>
      <div class="c-recommendedSearchTags_contentOverlay">
        <div class="c-showAll">
          <div
            class="c-showAll_button"
            @click="isClosed = !isClosed"
          >
            {{ showMoreText }}
            <CoconalaIcon
              class="c-showAll_icon"
              :name="isClosed ? 'chevron-down' : 'chevron-up'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ContentHeadline from '~/components/molecules/ContentHeadline'
import LinkRefProvider from '~/mixins/link-ref/LinkRefProvider'

/** 開閉ボタンが非表示になる高さの最大値 */
const DISABLED_HEIGHT_MAX = 143

/** @type {import('vue').ComponentOptions} */
const recommendedSearchTags = {
  name: 'RecommendedSearchTags',
  mixins: [LinkRefProvider],
  components: {
    ContentHeadline
  },
  props: {
    recommendedSearchTags: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isClosed: true,
      isDisabled: false
    }
  },
  computed: {
    /** 最初のタググループ */
    firstRecommendedSearchTag() {
      return this.recommendedSearchTags[0]
    },
    /** 2つ目以降タググループ */
    recommendedSearchTagAfterFirst() {
      return this.recommendedSearchTags.filter((_, i) => i > 0)
    },
    showMoreText() {
      return this.isClosed ? 'すべて見る' : '閉じる'
    }
  },
  methods: {
    getClasses() {
      return {
        'c-recommendedSearchTags-closed': !this.isDisabled && this.isClosed,
        'c-recommendedSearchTags-disabeld': this.isDisabled
      }
    },
    checkDisabled() {
      if (this.recommendedSearchTagAfterFirst.length > 0) {
        return
      }
      const rect = this.$refs.firstTagGroup?.getBoundingClientRect()
      this.isDisabled = rect && rect.height <= DISABLED_HEIGHT_MAX
    },
    getTagUrl(tagUrl) {
      // tagUrlはすでにクエリパラメータが付加されている想定
      return tagUrl + `&ref=${this.linkRef}`
    }
  },
  mounted() {
    this.checkDisabled()
    window.addEventListener('resize', this.checkDisabled, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkDisabled)
  }
}
export default recommendedSearchTags
</script>

<style lang="scss" scoped>
.c-recommendedSearchTags {
  &_content {
    position: relative;
    // 開閉ボタンの高さ(18px) + 上部余白(16px)
    padding-bottom: 34px;
    overflow: hidden;
  }
  &_tagGroups {
    position: relative;
    overflow: hidden;
  }
  &_contentOverlay {
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    .c-showAll {
      pointer-events: auto;
    }
  }
  &-closed {
    .c-recommendedSearchTags_content {
      // 開閉ボタンの高さ(18px) + 上部余白(4px)
      padding-bottom: 22px;
    }
    .c-recommendedSearchTags_tagGroups {
      // タググループのタイトル + margin + タグ1行分の高さ
      max-height: 77px;
    }
    .c-recommendedSearchTags_contentOverlay {
      .c-showAll {
        height: 22px;
      }
      &::after {
        content: '';
        display: block;
        position: absolute;
        bottom: 22px;
        width: 100%;
        height: 40px;
        background: linear-gradient(0deg, $color-white 0%, rgba($color-white, 0) 100%);
      }
    }
  }
  &-disabeld {
    .c-recommendedSearchTags_content {
      padding-bottom: 0;
    }
    .c-recommendedSearchTags_contentOverlay {
      display: none;
    }
  }
}

.c-tagGroup {
  margin-top: 16px;
  &_headline {
    font-size: 16px;
    line-height: 1.3;
  }
  &_list {
    display: flex;
    flex-wrap: wrap;
  }
  &_listItem {
    color: $color-font-base;
    font-size: 13px;
    line-height: 1.5;
    padding: 6px 20px;
    border-radius: 5px;
    border: 1px solid $color-gray-200;
    background-color: $color-white;
    margin-top: 8px;
    margin-right: 8px;
    @include link-effect;
  }
}
.c-showAll {
  pointer-events: auto;
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 34px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  background-color: $color-white;
  &_button {
    color: $color-link-base;
    font-size: 14px;
    line-height: 1.3;
    cursor: pointer;
  }
}

@media (max-width: breakpoint(Search, M)) {
  .c-recommendedSearchTags {
    padding: 0 12px;
    &_content {
      padding-bottom: 48px;
    }
    &_tagGroups {
      .c-tagGroup {
        margin-top: 16px;
      }
    }
    &-closed {
      .c-recommendedSearchTags_content {
        padding-bottom: 32px;
        max-height: 179px;
      }
      .c-recommendedSearchTags_tagGroups {
        max-height: none;
      }
      .c-recommendedSearchTags_contentOverlay {
        .c-showAll {
          height: 32px;
        }
        &::after {
          bottom: 28px;
          background: linear-gradient(0deg, $color-gray-50 0%, rgba($color-gray-50, 0) 100%);
        }
      }
    }
  }
  .c-showAll {
    background-color: $color-gray-50;
    height: 48px;
    &_button {
      font-size: 12px;
    }
  }
  .c-tagGroup {
    margin-top: 8px;
  }
}
</style>
